// src/components/LegacyMeetingCard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import './MeetingCard.css';
import { useVideoPopup } from './VideoPopupContext';
import Scorecard from './Scorecard';

function MeetingCard({
  id,
  agendaItems,
  date,
  title,
  topics,
  content,
  isUpcoming,
  meetingType,
  initialExpanded = false,
  youtubeLink,
  part,
  isWidget = false,
  onExpand, // New Prop
  recordingNotice = null
}) {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [linkedVotes, setLinkedVotes] = useState({});
  const { showPopup } = useVideoPopup();
  const location = useLocation();
  const contentRef = useRef(null);

  // --- SAFE NOTIFICATION: Only trigger when 'expanded' changes to true ---
  useEffect(() => {
    if (expanded && onExpand) {
        onExpand(agendaItems, youtubeLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]); // Exclude onExpand to prevent infinite update loops

  // --- 1. Fetch Vote Links ---
  useEffect(() => {
    if (!id || !expanded) return;
    async function fetchVoteLinks() {
      const { data } = await supabase
        .from('meeting_items')
        .select('item_index, voting_id')
        .eq('meeting_id', id)
        .not('voting_id', 'is', null);

      if (data) {
        const links = {};
        data.forEach(row => {
          if (row.item_index !== null) {
            links[row.item_index - 1] = row.voting_id;
          }
        });
        setLinkedVotes(links);
      }
    }
    fetchVoteLinks();
  }, [id, expanded]);

  // --- 2. Widget Mode: Force External Links to New Tab ---
  useEffect(() => {
    if (isWidget && expanded && contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        if (link.classList.contains('timestamp-link')) return;
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, [isWidget, expanded, agendaItems, content]);

  // --- Helpers ---
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  };

  const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

  const computedTitle =
  meetingType === 'cboc'
    ? `Citizens' Bond Oversight Committee ${title}`
    : meetingType === 'study session'
      ? `Study Session ${title}`
      : meetingType === 'work session'
        ? `Work Session ${title}`
        : (meetingType === 'special' || meetingType === 'upcoming')
          ? `${capitalize(meetingType)} ${title}`
          : title;

  const formattedTopics = Array.isArray(topics) ? topics.join(', ') : topics;

  const safeDate = date || 'unknown';
  const baseId = `meeting-${safeDate.replace(/\//g, '-')}`;
  const meetingId = !isUpcoming
    ? (part !== null && part !== undefined ? `${baseId}-p${part}` : baseId)
    : undefined;

  // --- Auto-Expand ---
  useEffect(() => {
    const hash = location.hash.substring(1);
    if (!isUpcoming && meetingId && hash.startsWith(meetingId)) {
      setExpanded(true);
    }
  }, [location.hash, meetingId, isUpcoming]);

  // --- Auto-Scroll ---
  useEffect(() => {
    if (expanded && location.hash) {
      const hash = location.hash.substring(1);
      if (hash.startsWith(meetingId)) {
        setTimeout(() => {
          const targetElement = document.getElementById(hash);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            targetElement.classList.add('highlight-flash');
            setTimeout(() => targetElement.classList.remove('highlight-flash'), 2000);
          }
        }, 300);
      }
    }
  }, [expanded, location.hash, meetingId]);

  // --- Timestamp Logic ---
  const processContentWithTimestamps = (text) => {
    if (!text || typeof text !== 'string') return text || '';
    if (!youtubeLink) return text;
    const timestampRegex = /\b(\d{1,2}:\d{2}:\d{2}|\d{1,2}:\d{2})\b/g;
    return text.replace(timestampRegex, (match) => {
      return `<span class="timestamp-link" data-timestamp="${match}">${match}</span>`;
    });
  };

  const handleTimestampClick = useCallback((event) => {
    const timestamp = event.target.getAttribute('data-timestamp');
    // We pass event.target to help position logic if needed, though mostly unused in new layout
    if (timestamp && youtubeLink) showPopup(timestamp, youtubeLink, event.target);
  }, [showPopup, youtubeLink]);

  // Delegated click handler: reliably fires for every .timestamp-link inside
  // the card, including dynamically-rendered spans from dangerouslySetInnerHTML.
  const handleContentClick = useCallback((event) => {
    const target = event.target.closest && event.target.closest('.timestamp-link');
    if (!target) return;
    const timestamp = target.getAttribute('data-timestamp');
    if (timestamp && youtubeLink) showPopup(timestamp, youtubeLink, target);
  }, [showPopup, youtubeLink]);

  useEffect(() => {
    if (!expanded || !youtubeLink || !meetingId) return;
    setTimeout(() => {
      const cardElement = document.getElementById(meetingId);
      if (!cardElement) return;
      const timestampElements = cardElement.querySelectorAll('.timestamp-link');
      timestampElements.forEach((el) => {
        // VIDEO TRIGGER: Click only
        el.addEventListener('click', handleTimestampClick); 
      });
      return () => {
        timestampElements.forEach((el) => {
          el.removeEventListener('click', handleTimestampClick);
        });
      };
    }, 200);
  }, [expanded, meetingId, youtubeLink, handleTimestampClick, agendaItems]);

  const renderStructuredItems = () => {
    if (!agendaItems || !Array.isArray(agendaItems)) return null;
    return agendaItems.map((item, index) => {
      const itemId = `${meetingId}-item-${index + 1}`;
      let debateContent = null;
      if (item.summary?.debate) {
        if (typeof item.summary.debate === 'string') {
          debateContent = item.summary.debate;
        } else {
          debateContent = (
            <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '20px', listStyleType: 'square' }}>
              {item.summary.debate.for && item.summary.debate.for.length > 0 && (
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>For:</strong>{' '}
                  <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.debate.for.join(' ')) }} />
                </li>
              )}
              {item.summary.debate.against && item.summary.debate.against.length > 0 && (
                <li>
                  <strong>Against:</strong>{' '}
                  <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.debate.against.join(' ')) }} />
                </li>
              )}
            </ul>
          );
        }
      }

      // --- NEW TIMELINE LOGIC (Start / Motion / Vote / End) ---
      const start = item.timestamp_start;
      const end = item.timestamp_end;
      const { motion, vote } = item.action_timeline || {};
      
      const hasMotion = motion?.present && motion?.timestamp;
      const hasVote = vote?.present && vote?.timestamp;
      
      const timelineDisplay = (!start && !end && !hasMotion && !hasVote) ? null : (
        <div className="timeline-box" style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '15px', 
          marginTop: '8px',
          marginBottom: '12px', 
          fontSize: '0.9rem', 
          color: '#475569', 
          background: '#f8fafc', 
          padding: '6px 12px', 
          borderRadius: '4px', 
          width: 'fit-content', 
          border: '1px solid #e2e8f0',
          alignItems: 'center'
        }}>
          {start && (
             <span>
               <strong>Start:</strong> <span className="timestamp-link" data-timestamp={start}>{start}</span>
             </span>
          )}
          {hasMotion && (
            <span>
              <strong>Motion:</strong> <span className="timestamp-link" data-timestamp={motion.timestamp}>{motion.timestamp}</span>
            </span>
          )}
          {hasVote && (
            <span style={{ color: '#0f172a', fontWeight: '600' }}>
              <strong>Vote:</strong> <span className="timestamp-link" data-timestamp={vote.timestamp}>{vote.timestamp}</span>
            </span>
          )}
          {end && (
             <span>
               <strong>End:</strong> <span className="timestamp-link" data-timestamp={end}>{end}</span>
             </span>
          )}
        </div>
      );

      const isPublicComment = item.title && item.title.includes("Public Comment");

      // --- Budget Details Block (merged from budget_discussions by timestamp_start) ---
      const renderBudgetDetail = () => {
        const b = item.budget_detail;
        if (!b) return null;
        const figures = Array.isArray(b.key_figures) ? b.key_figures : [];
        const fiscalYear = b.fiscal_year;
        const typeLabel = b.type
          ? b.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          : null;

        if (!figures.length && !b.next_steps && !fiscalYear && !typeLabel) return null;

        return (
          <div className="budget-details" style={{
            marginTop: '1rem',
            marginLeft: '40px',
            padding: '12px 16px',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: '6px',
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: (figures.length || b.next_steps) ? '10px' : 0,
            }}>
              <span style={{ fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>
                Budget & Financial Details
              </span>
              {fiscalYear && (
                <span style={{ background: '#059669', color: 'white', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>
                  {fiscalYear}
                </span>
              )}
              {typeLabel && (
                <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>
                  {typeLabel}
                </span>
              )}
            </div>
            {figures.length > 0 && (
              <div>
                <strong style={{ color: '#065f46', fontSize: '0.88rem' }}>Key Figures:</strong>
                <ul style={{ marginTop: '4px', marginBottom: b.next_steps ? '10px' : 0, paddingLeft: '20px', listStyleType: 'disc', color: '#0f172a', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  {figures.map((fig, i) => {
                    const figureText = typeof fig === 'string' ? fig : fig?.figure;
                    const figTs = (fig && typeof fig === 'object') ? fig.timestamp : null;
                    if (!figureText) return null;
                    return (
                      <li key={i} style={{ marginBottom: '2px' }}>
                        <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(figureText) }} />
                        {figTs && (
                          <>
                            {' '}
                            <span
                              className="timestamp-link"
                              data-timestamp={figTs}
                              style={{ marginLeft: '4px', fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}
                            >
                              [{figTs}]
                            </span>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {b.next_steps && (
              <div style={{ fontSize: '0.88rem', color: '#0f172a' }}>
                <strong style={{ color: '#065f46' }}>Next Steps:</strong>{' '}
                <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(b.next_steps) }} />
              </div>
            )}
          </div>
        );
      };

      return (
        <div
          key={index}
          id={itemId}
          className="meeting-item meeting-item-target"
          style={{ marginBottom: '2.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '2rem' }}
        >
          <h4 style={{ margin: '0 0 6px 0', fontSize: '1.25rem', fontWeight: '700', color: '#0f172a' }}>
            {index + 1}. {item.title}
          </h4>
          
          {/* Insert the new timeline box here */}
          {timelineDisplay}

          <div style={{ paddingLeft: '40px', marginTop: '0.75rem', lineHeight: '1.7', color: '#1e293b' }}>
            {item.summary?.context && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>{isPublicComment ? "Speaker Tally" : "Context"}: </strong>
                <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.context) }} />
              </div>
            )}
            {item.summary?.impact && Array.isArray(item.summary.impact) && item.summary.impact.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>{isPublicComment ? "Themes" : "Impact"}:</strong>
                <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '20px', listStyleType: 'square' }}>
                  {item.summary.impact.map((theme, idx) => (
                    <li key={idx} style={{ marginBottom: '0.25rem' }}>
                      <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(theme) }} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {isPublicComment && item.summary?.speakers && Array.isArray(item.summary.speakers) && item.summary.speakers.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Speakers:</strong>
                <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem', paddingLeft: '20px', listStyleType: 'square' }}>
                  {item.summary.speakers.map((spk, idx) => (
                    <li key={idx} style={{ marginBottom: '0.5rem' }}>
                      <strong>{spk.role || 'Speaker'}</strong>
                      {' - '}
                      <strong>Time: </strong>
                      <span className="timestamp-link" data-timestamp={spk.timestamp}>{spk.timestamp}</span>
                      {' - '}
                      <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(spk.summary) }} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!isPublicComment && debateContent && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Debate:</strong>
                {typeof debateContent === 'string' ? (
                  <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(debateContent) }} />
                ) : (
                  debateContent
                )}
              </div>
            )}
            {item.summary?.decision && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Decision / Next: </strong>
                <span dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(item.summary.decision) }} />
              </div>
            )}
          </div>
          {renderBudgetDetail()}
          {linkedVotes[index] && (
            <div className="mt-4" style={{ marginTop: '1.5rem', marginLeft: '40px' }}>
              <Scorecard votingId={linkedVotes[index]} />
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      <div className="meeting-card" id={meetingId}>
        <div
          className="meeting-header"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded(!expanded);
            }
          }}
        >
          <div className="meeting-info">
            <h3 className="meeting-title">
              {computedTitle} {formatDate(date)}
              {part !== null && part !== undefined && ` - Part ${part}`}
              {isUpcoming && <span className="upcoming-badge">Upcoming</span>}
              {recordingNotice && <span className="no-recording-badge" title="The district did not make a recording publicly available">No District Recording</span>}
            </h3>
            <div className="meeting-subheading">Topics: {formattedTopics}</div>
          </div>
          <button
            className="expand-button"
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          >
            {expanded ? 'Minimize' : 'Expand'}
          </button>
        </div>

        {expanded && (
          <div className="meeting-content mt-4" ref={contentRef} onClick={handleContentClick}>
            {recordingNotice && (
              <div className="recording-notice-banner" role="note">
                <span className="recording-notice-label">Notice — No District Recording</span>
                <span className="recording-notice-text">{recordingNotice}</span>
              </div>
            )}
            {agendaItems && agendaItems.length > 0 ? (
              renderStructuredItems()
            ) : (
              <div dangerouslySetInnerHTML={{ __html: processContentWithTimestamps(content) }} />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default MeetingCard;
